/**
 * Remove a single invoice payment and reverse the side-effects that
 * RecordPaymentDialog applies on creation:
 *
 *   1. Invoice status — a payment that fully settled the invoice flips it to
 *      'paid'. If removing this payment leaves a balance, drop a 'paid' invoice
 *      back to 'issued'.
 *   2. Treatment-record mirror (PRD.218 §10.1 / PRD.245) — full settlement
 *      mirrors `amount_paid_cents = amount_charged_cents` onto linked treatment
 *      records. Re-sync that stored value to the post-deletion settlement state.
 *
 * Item-level paid/balance shown in the Treatment Record view is DERIVED from
 * `invoice_payments.allocations` at read time, so it updates automatically once
 * the payment row is gone — no extra work needed here.
 *
 * PRD.261 — patient package payment_status is also re-derived after removing a
 * payment. Redemption activation is intentionally sticky (never auto-reverted),
 * matching the "activate after first payment" rule.
 */

import { syncPatientPackagePaymentStatusFromInvoice } from './patient-package-service';
import { recomputeInvoiceItemPaymentStatus } from './invoice-item-settlement';

export interface DeletePaymentParams {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  db: any;
  paymentId: string;
}

export interface DeletePaymentResult {
  deleted: boolean;
  error?: string;
}

export async function deletePayment({
  db,
  paymentId,
}: DeletePaymentParams): Promise<DeletePaymentResult> {
  // 1. Verify the payment exists and capture its invoice link.
  const rows = (await db.getAll(`SELECT id, invoice_id FROM invoice_payments WHERE id = ?`, [
    paymentId,
  ])) as { id: string; invoice_id: string | null }[];
  if (rows.length === 0) {
    return { deleted: false, error: 'Payment not found' };
  }
  const invoiceId = rows[0].invoice_id;
  const now = new Date().toISOString();

  // 2. Delete the payment row.
  await db.execute(`DELETE FROM invoice_payments WHERE id = ?`, [paymentId]);

  // 3. Reverse invoice-level side-effects (only meaningful when invoice-linked).
  if (invoiceId) {
    const invRows = (await db.getAll(`SELECT id, status, grand_total FROM invoices WHERE id = ?`, [
      invoiceId,
    ])) as { id: string; status: string | null; grand_total: number | null }[];

    if (invRows.length > 0) {
      const inv = invRows[0];
      const grandTotalCents = Math.round((inv.grand_total ?? 0) * 100);

      const paidRows = (await db.getAll(
        `SELECT COALESCE(SUM(amount_cents), 0) AS paid FROM invoice_payments WHERE invoice_id = ?`,
        [invoiceId],
      )) as { paid: number }[];
      const paidCents = paidRows[0]?.paid ?? 0;
      const fullySettled = grandTotalCents > 0 && paidCents >= grandTotalCents;

      // Invoice no longer fully paid → revert a 'paid' invoice to 'issued'.
      if (!fullySettled && inv.status === 'paid') {
        await db.execute(`UPDATE invoices SET status = ?, updated_at = ? WHERE id = ?`, [
          'issued',
          now,
          invoiceId,
        ]);
      }

      // Re-sync the stored treatment-record mirror to the settlement state:
      // charged when fully settled, otherwise cleared (item-level partial paid
      // is reflected via derived allocations, not this stored field).
      const linked = (await db.getAll(
        `SELECT id, amount_charged_cents FROM patient_treatment_records WHERE invoice_id = ?`,
        [invoiceId],
      )) as { id: string; amount_charged_cents: number | null }[];
      for (const rec of linked) {
        const nextPaid = fullySettled ? (rec.amount_charged_cents ?? 0) : 0;
        await db.execute(
          `UPDATE patient_treatment_records SET amount_paid_cents = ?, updated_at = ? WHERE id = ?`,
          [nextPaid, now, rec.id],
        );
      }

      // PRD.261 — re-derive patient package payment_status from the reduced
      // paid total (redemption activation stays sticky).
      await syncPatientPackagePaymentStatusFromInvoice({ db, invoiceId });

      // Removing a payment can un-settle items → recompute per-item status so
      // pharmacy dispense eligibility reverts in step.
      await recomputeInvoiceItemPaymentStatus(db, invoiceId);
    }
  }

  return { deleted: true };
}
